// Suche ⌘K der Pille – reine Funktionen: welche Einträge in welcher Reihenfolge erscheinen
// (Handoff Pille §5) und wie die Tastatur die Auswahl bewegt.

export type SuchAbschnitt = 'Zuletzt' | 'Seiten' | 'Aktionen' | 'Top-Treffer' | 'Kontakte' | 'Treffer' | 'E-Mails' | 'Dokumente' | 'Befehle';

export interface SuchTreffer { id: string; kind: 'contact' | 'hit'; type: string }
export interface SuchBefehl { id: string }

export type SuchEintrag<B extends SuchBefehl, R, H extends SuchTreffer> =
  | { art: 'befehl'; befehl: B; abschnitt: SuchAbschnitt }
  | { art: 'zuletzt'; zuletzt: R; abschnitt: SuchAbschnitt }
  | { art: 'treffer'; treffer: H; abschnitt: SuchAbschnitt };

/** Abschnitt eines Treffers: Kontakte, E-Mails, Dokumente, sonst „Treffer“. */
export function trefferAbschnitt(h: SuchTreffer): SuchAbschnitt {
  if (h.kind === 'contact') return 'Kontakte';
  if (h.type === 'email') return 'E-Mails';
  if (h.type === 'dokument') return 'Dokumente';
  return 'Treffer';
}

const GRUPPEN: readonly SuchAbschnitt[] = ['Kontakte', 'Treffer', 'E-Mails', 'Dokumente'];

/**
 * Flache Liste in Anzeige- und Tastaturreihenfolge.
 * Ohne Eingabe: Zuletzt, Seiten (Navigation), Aktionen („Neu …“).
 * Mit Eingabe: Top-Treffer (erster Treffer nach Server-Ranking), dann die Gruppen in fester
 * Reihenfolge (innerhalb wie geliefert), Befehle zuletzt.
 */
export function baueSuchListe<B extends SuchBefehl, R, H extends SuchTreffer>(p: {
  query: string;
  zuletzt: readonly R[];
  seiten: readonly B[];
  aktionen: readonly B[];
  befehle: readonly B[];
  treffer: readonly H[];
}): SuchEintrag<B, R, H>[] {
  if (p.query.trim().length === 0) {
    return [
      ...p.zuletzt.map(z => ({ art: 'zuletzt' as const, zuletzt: z, abschnitt: 'Zuletzt' as const })),
      ...p.seiten.map(b => ({ art: 'befehl' as const, befehl: b, abschnitt: 'Seiten' as const })),
      ...p.aktionen.map(b => ({ art: 'befehl' as const, befehl: b, abschnitt: 'Aktionen' as const })),
    ];
  }
  const liste: SuchEintrag<B, R, H>[] = [];
  const [top, ...rest] = p.treffer;
  if (top) liste.push({ art: 'treffer', treffer: top, abschnitt: 'Top-Treffer' });
  for (const g of GRUPPEN) {
    for (const h of rest) if (trefferAbschnitt(h) === g) liste.push({ art: 'treffer', treffer: h, abschnitt: g });
  }
  for (const b of p.befehle) liste.push({ art: 'befehl', befehl: b, abschnitt: 'Befehle' });
  return liste;
}

/** ↑/↓ bewegen die Auswahl, an den Enden wird begrenzt (kein Umlauf). */
export function naechsteAuswahl(aktuell: number, taste: 'ArrowUp' | 'ArrowDown', anzahl: number): number {
  if (anzahl <= 0) return 0;
  const n = taste === 'ArrowDown' ? aktuell + 1 : aktuell - 1;
  return Math.max(0, Math.min(n, anzahl - 1));
}

/** Gestaffeltes Einblenden: 24 ms je Eintrag, ab dem 16. gleichzeitig. */
export function einblendVerzoegerung(index: number): number {
  return Math.min(index, 16) * 0.024;
}
